import React, { useEffect, useMemo } from "react";
import { GlassCard, Button } from "../components/ui";
import { btn3d } from "../styles/buttonStyles";
import ItineraryDisplay from "../components/ItineraryDisplay";
import MapView from "../components/MapView";
import ChecklistView from "../components/ChecklistView";
import { getVerifiedSpotMatches } from "../utils/placeVerification";

export default function ResultPage({ darkMode, lang, setStep, personal, dm, travel, travelNights, downloadPDF, pdfLoading, communityShared, shareToCommunity, setCommunityShared, setLinkCopied, linkCopied, spotsLoading, spotsProgress, spots, setResultView, resultView, itinerary, handleItinerarySpotClick, chatMessages, setChatInput, chatLoading, chatBottomRef, chatInput, sendChatMessage, t, checklist, setChecklist, checklistLoading, checklistProgress, generateChecklist, setSelectedSpotIdx, selectedSpotIdx, tourItems, tourMapPins, tourLoading, tourLoaded, tourError, fetchTourAPI, tripSaveError, setItinerary, setSpots, setSpotsProgress, setSpotsTotal, setTripSaved, setPersonal, setTravel }) {
  useEffect(() => {
    fetchTourAPI("tourist");
  }, [fetchTourAPI]);

  const verifiedMatches = useMemo(
    () => getVerifiedSpotMatches(spots, tourItems, itinerary),
    [spots, tourItems, itinerary]
  );
  const hasVerifiedMatches =
    !tourLoading &&
    tourLoaded?.tourist &&
    !tourError &&
    verifiedMatches.length > 0;

  return (
            <div className="animate-in">
              <GlassCard>
                {/* Title row */}
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:7 }}>
                  <div className="text-xl">🗺️</div>
                  <div style={{ flex:1 }}>
                    <h2 className="text-md" style={{ fontWeight:800, color:"var(--accent)", letterSpacing:"-0.01em" }}>{personal.name ? `${personal.name}님의 여행 일정` : "나의 여행 일정"}</h2>
                    <p className="text-xs" style={{ color:dm?"rgba(226,232,240,0.70)":"rgba(51,65,85,0.74)" }}>{travel.destination} | {travelNights===0?"당일여행":`${travelNights}박 ${travelNights+1}일`} | {travel.startDate}{travel.startDate!==travel.endDate?` ~ ${travel.endDate}`:""}</p>
                  </div>
                  {/* PDF Download Button */}
                  <button type="button" onClick={downloadPDF} disabled={pdfLoading} title={pdfLoading?"PDF 생성 중...":"PDF 파일로 다운로드"} aria-label={pdfLoading?"PDF 생성 중":"여행 일정을 PDF로 다운로드"}
                    className="btn3d"
                    style={{ ...btn3d(pdfLoading?"teal":"outline", dm), display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:2, padding:"8px 11px", borderRadius:12, cursor:pdfLoading?"not-allowed":"pointer", flexShrink:0, minWidth:50 }}>
                    {pdfLoading
                      ? <div style={{ width:18, height:18, border:"2px solid rgba(45,212,191,0.4)", borderTopColor:"#2DD4BF", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
                      : <span className="text-md">📄</span>
                    }
                    <span className="text-xs" style={{ fontWeight:800, color:pdfLoading?"#2DD4BF":"var(--accent)", letterSpacing:"0.05em" }}>{pdfLoading?"생성중":"PDF"}</span>
                  </button>
                </div>

                {/* Tags */}
                <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:14 }}>
                  {[
                    personal.gender,
                    personal.bloodType,
                    personal.mbti,
                    personal.zodiac ? personal.zodiac.split(" ")[0] : "",
                    travel.style,
                    travel.transport
                  ].filter(Boolean).map((tag,i)=>(
                    <span key={i} className="text-xs" style={{ padding:"3px 10px", borderRadius:100, background:dm?"rgba(167,139,250,0.1)":"rgba(255,255,255,0.75)", border:dm?"1px solid rgba(167,139,250,0.28)":"1px solid rgba(99,102,241,0.3)", color:"var(--accent)", fontWeight:700 }}>{tag}</span>
                  ))}
                </div>

                {hasVerifiedMatches && (
                  <div
                    role="status"
                    aria-live="polite"
                    className="text-xs"
                    style={{
                      display:"flex",
                      alignItems:"center",
                      gap:7,
                      marginBottom:14,
                      padding:"9px 11px",
                      borderRadius:10,
                      background:dm?"rgba(45,212,191,0.09)":"rgba(240,253,250,0.95)",
                      border:dm?"1px solid rgba(45,212,191,0.24)":"1px solid rgba(20,184,166,0.25)",
                      color:dm?"#5EEAD4":"#0F766E",
                      fontWeight:800,
                      lineHeight:1.5,
                    }}
                  >
                    <span aria-hidden="true">🛡️</span>
                    <span>{verifiedMatches.length}곳이 실제 관광 데이터와 일치합니다</span>
                  </div>
                )}

                {tripSaveError && (
                  <div role="alert" className="text-sm" style={{ marginBottom:14, padding:"10px 14px", borderRadius:10, background:dm?"rgba(255,90,80,0.12)":"rgba(255,230,227,0.85)", border:"1px solid rgba(200,60,45,0.35)", fontWeight:700, color:dm?"#FF8F80":"#C0392B", textAlign:"center" }}>
                    ⚠️ {tripSaveError}
                  </div>
                )}

                {/* 액션 버튼 */}
                <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:14 }}>
                  {/* 1행: 커뮤니티 공유 (전체 너비) */}
                  <div style={{ display:"flex", gap:8 }}>
                    {/* 커뮤니티에 공유하기 */}
                    <button onClick={async () => {
                      if(communityShared) return;
                      await shareToCommunity();
                      setCommunityShared(true);
                    }}
                      className="btn3d text-sm" style={{ ...btn3d("outline", dm), flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:6, padding:"12px 10px", minHeight:46, borderRadius:12, fontWeight:800, cursor:communityShared?"default":"pointer" }}>
                      <span>{communityShared ? "🌟" : "🌍"}</span>
                      <span>{communityShared ? "커뮤니티 공개됨" : "커뮤니티에 공유하기"}</span>
                    </button>
                  </div>
                  {/* 2행: 링크 복사 */}
                  <button onClick={async () => {
                    const url = window.location.href;
                    try { await navigator.clipboard.writeText(url); }
                    catch(e) {
                      const el = document.createElement("textarea");
                      el.value = url; el.style.position="fixed"; el.style.opacity="0";
                      document.body.appendChild(el); el.select();
                      document.execCommand("copy"); document.body.removeChild(el);
                    }
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 2500);
                  }}
                    className="btn3d text-sm" style={{ ...btn3d("teal", dm), width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"12px", minHeight:46, borderRadius:12, fontWeight:800 }}>
                    <span className="text-base">{linkCopied ? "✅" : "🔗"}</span>
                    <span>{linkCopied ? "링크 복사됨! SNS에 붙여넣기 하세요" : "여행일정 링크 복사 (SNS 공유용)"}</span>
                  </button>
                </div>

                {/* Tab switcher */}
                <div style={{ display:"flex", gap:0, marginBottom:14, background:dm?"rgba(167,139,250,0.08)":"rgba(99,102,241,0.12)", border:dm?"1px solid rgba(167,139,250,0.2)":"1px solid rgba(99,102,241,0.22)", borderRadius:12, padding:4 }}>
                  {[
                    {key:"itinerary", label:lang==="ko"?"📋 일정표":"📋 Itinerary"},
                    {key:"map", label: spotsLoading ? `🗺️ 지도 (${spotsProgress}%)` : spots.filter(s=>s.lat).length>0 ? `🗺️ 지도 (${spots.filter(s=>s.lat).length})` : "🗺️ 지도"},
                    {key:"checklist", label:"📋 체크리스트"}
                  ].map(tab=>(
                    <button key={tab.key} type="button" onClick={()=>{
                      setResultView(tab.key);
                      if (tab.key === "map") fetchTourAPI("tourist");
                    }}
                      aria-pressed={resultView===tab.key}
                      className="btn3d text-xs" style={{ ...btn3d(resultView===tab.key?"primary":"outline", dm), flex:1, minWidth:0, padding:"9px 2px", minHeight:40, borderRadius:9, fontWeight:800 }}>
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Content */}
                {resultView==="itinerary"&&(
                  <ItineraryDisplay content={itinerary} spots={spots.filter(s=>s.lat)} tourItems={tourItems} onSpotClick={handleItinerarySpotClick} darkMode={darkMode}/>
                )}
                {resultView==="itinerary"&&(
                  <div style={{ marginTop:20 }}>
                    {/* 채팅 헤더 */}
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                      <div className="text-base" style={{ width:32, height:32, borderRadius:10, background:"linear-gradient(135deg,var(--accent),var(--accent2))", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>✏️</div>
                      <div>
                        <p className="text-sm" style={{ fontWeight:800, color:"var(--accent)" }}>일정 수정 채팅</p>
                        <p className="text-xs" style={{ color:dm?"rgba(226,232,240,0.68)":"rgba(51,65,85,0.68)" }}>원하는 변경사항을 자유롭게 말씀해주세요</p>
                      </div>
                    </div>

                    {/* 예시 칩 */}
                    {chatMessages.length === 0 && (
                      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:12 }}>
                        {["첫째날 조식을 10시로 변경해줘","둘째날 오후에 쇼핑 시간 추가해줘","마지막날 일찍 체크아웃으로 바꿔줘","예산을 좀 더 절약하는 방향으로 수정해줘"].map((ex, i) => (
                          <button key={i} type="button" onClick={() => setChatInput(ex)}
                            className="btn3d text-xs" style={{ ...btn3d("outline", dm), padding:"6px 10px", minHeight:32, borderRadius:100, fontWeight:700 }}>
                            {ex}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* 채팅 메시지 목록 */}
                    {chatMessages.length > 0 && (
                      <div style={{ maxHeight:280, overflowY:"auto", display:"flex", flexDirection:"column", gap:10, marginBottom:12, padding:"4px 0" }}>
                        {chatMessages.map((msg, i) => (
                          <div key={i} style={{ display:"flex", justifyContent: msg.role==="user" ? "flex-end" : "flex-start", alignItems:"flex-end", gap:7 }}>
                            {msg.role==="ai" && (
                              <div className="text-base" style={{ width:28, height:28, borderRadius:8, background:"linear-gradient(135deg,var(--accent),var(--accent2))", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginBottom:2 }}>✈️</div>
                            )}
                            <div className="text-sm" style={{
                              maxWidth:"78%", padding:"10px 14px", borderRadius: msg.role==="user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                              background: msg.role==="user" ? "var(--accent)" : dm ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.85)",
                              border: msg.role==="user" ? "none" : dm ? "1px solid rgba(167,139,250,0.22)" : "1px solid rgba(99,102,241,0.2)",
                              lineHeight:1.6,
                              color: msg.role==="user" ? "#fff" : "var(--text)",
                              boxShadow: msg.role==="user" ? "0 4px 14px rgba(99,102,241,0.25)" : "0 2px 8px rgba(0,0,0,0.06)"
                            }}>
                              {msg.role==="ai" && <span className="text-xs" style={{ fontWeight:800, color:"var(--accent)", display:"block", marginBottom:4 }}>✅ 수정 완료</span>}
                              {msg.content}
                            </div>
                            {msg.role==="user" && (
                              <div className="text-sm" style={{ width:28, height:28, borderRadius:8, background:dm?"rgba(238,242,255,0.2)":"rgba(238,242,255,0.9)", border:"1px solid rgba(167,139,250,0.35)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontWeight:800, color:"var(--accent)", marginBottom:2 }}>
                                {personal.name ? personal.name.charAt(0) : "나"}
                              </div>
                            )}
                          </div>
                        ))}
                        {chatLoading && (
                          <div style={{ display:"flex", alignItems:"flex-end", gap:7 }}>
                            <div className="text-base" style={{ width:28, height:28, borderRadius:8, background:"linear-gradient(135deg,var(--accent),var(--accent2))", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>✈️</div>
                            <div style={{ padding:"10px 16px", borderRadius:"16px 16px 16px 4px", background:dm?"rgba(255,255,255,0.08)":"rgba(255,255,255,0.85)", border:dm?"1px solid rgba(167,139,250,0.22)":"1px solid rgba(99,102,241,0.2)", display:"flex", alignItems:"center", gap:5 }}>
                              {[0,1,2].map(i => (
                                <div key={i} style={{ width:7, height:7, borderRadius:"50%", background:"var(--accent)", animation:"pulse 1.2s ease-in-out infinite", animationDelay:`${i*0.2}s` }}/>
                              ))}
                            </div>
                          </div>
                        )}
                        <div ref={chatBottomRef}/>
                      </div>
                    )}

                    {/* 채팅 입력창 */}
                    <div style={{ display:"flex", gap:8, alignItems:"flex-end" }}>
                      <div style={{ flex:1, position:"relative" }}>
                        <label htmlFor="itinerary-chat-input" className="text-xs" style={{ display:"block", color:"var(--muted)", fontWeight:700, marginBottom:5 }}>
                          일정 수정 요청
                        </label>
                        <textarea
                          id="itinerary-chat-input"
                          value={chatInput}
                          onChange={e => setChatInput(e.target.value)}
                          onKeyDown={e => { if(e.key==="Enter" && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }}
                          placeholder={t.editPlaceholder}
                          rows={2}
                          className="text-sm" style={{ width:"100%", padding:"11px 14px", background:dm?"rgba(255,255,255,0.06)":"rgba(255,255,255,0.85)", border:dm?"1px solid rgba(167,139,250,0.22)":"1px solid rgba(99,102,241,0.25)", borderRadius:14, color:"var(--text)", fontFamily:"inherit", resize:"none", outline:"none", lineHeight:1.5, boxSizing:"border-box", transition:"border 0.2s" }}
                          onFocus={e=>e.target.style.borderColor="var(--accent)"}
                          onBlur={e=>e.target.style.borderColor=dm?"rgba(167,139,250,0.22)":"rgba(99,102,241,0.25)"}
                        />
                      </div>
                      <button type="button" onClick={sendChatMessage} disabled={!chatInput.trim()||chatLoading}
                        aria-label={chatLoading?"일정 수정 요청 전송 중":"일정 수정 요청 보내기"}
                        className="btn3d text-lg" style={{ ...btn3d(chatInput.trim()&&!chatLoading ? "primary" : "outline", dm), width:46, height:46, borderRadius:13, opacity: chatInput.trim()&&!chatLoading ? 1 : 0.55, cursor: chatInput.trim()&&!chatLoading ? "pointer" : "not-allowed", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        {chatLoading ? (
                          <div style={{ width:18, height:18, border:"2px solid rgba(255,255,255,0.4)", borderTopColor:"#fff", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
                        ) : "↑"}
                      </button>
                    </div>
                    <p className="text-xs" style={{ color:dm?"rgba(226,232,240,0.60)":"rgba(51,65,85,0.65)", marginTop:6, textAlign:"center" }}>Shift+Enter로 줄바꿈 · 수정 후 위 일정표에 즉시 반영됩니다</p>
                  </div>
                )}
                {resultView==="checklist"&&(
                  <ChecklistView
                    travel={travel} personal={personal}
                    checklist={checklist} setChecklist={setChecklist}
                    loading={checklistLoading}
                    loadingProgress={checklistProgress}
                    onGenerate={generateChecklist}
                    darkMode={darkMode} t={t} lang={lang}
                  />
                )}
                {resultView==="map"&&(
                  <div>
                    {tourError && (
                      <div role="alert" style={{ marginBottom:12, padding:"10px 12px", borderRadius:10, textAlign:"center", background:dm?"rgba(255,90,80,0.10)":"rgba(255,230,227,0.75)", border:"1px solid rgba(200,60,45,0.3)" }}>
                        <p className="text-xs" style={{ color:dm?"#FF8F80":"#C0392B", fontWeight:800, marginBottom:8 }}>⚠️ 관광지 정보를 불러오지 못했습니다: {tourError}</p>
                        <button onClick={() => fetchTourAPI("tourist")} className="btn3d text-xs" style={{ ...btn3d("danger", dm), padding:"7px 13px", minHeight:36, borderRadius:10, fontWeight:800 }}>
                          다시 불러오기
                        </button>
                      </div>
                    )}
                    {spots.length === 0 && spotsLoading ? (
                      <div style={{ padding:"48px 0", textAlign:"center" }}>
                        <div style={{ position:"relative", width:90, height:90, margin:"0 auto 18px" }}>
                          <div style={{ position:"absolute", inset:0, borderRadius:"50%", border:"3px solid transparent", borderTopColor:"var(--accent)", animation:"spin 1s linear infinite" }}/>
                          <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                            <span className="text-lg" style={{ fontWeight:900, color:"#38bdf8" }}>{spotsProgress}</span>
                            <span className="text-xs" style={{ color:"rgba(167,139,250,0.7)", fontWeight:700 }}>%</span>
                          </div>
                        </div>
                        <p className="text-base" style={{ color:"var(--accent)", fontWeight:700, marginBottom:4 }}>지도 데이터 준비 중...</p>
                        <p className="text-sm" style={{ color:dm?"rgba(226,232,240,0.65)":"rgba(67,56,202,0.70)" }}>잠시만 기다려주세요</p>
                      </div>
                    ) : (
                      <MapView key={`map-${spots.map(s=>s.lat+s.lng).join("")}`} spots={spots.filter(s=>s.lat&&s.lng)} destination={travel.destination} onSpotSelect={setSelectedSpotIdx} highlightIdx={selectedSpotIdx} darkMode={darkMode} tourPins={tourMapPins}/>
                    )}
                  </div>
                )}

                {/* Actions — 후기 쓰기를 주 동작으로, 보조 동작은 아래 줄 */}
                <div style={{ marginTop:16 }}>
                  <button onClick={()=>setStep("review")} className="btn3d text-base" style={{ ...btn3d("primary", dm), width:"100%", padding:"14px 16px", minHeight:52, borderRadius:15, fontWeight:800, letterSpacing:"0.02em", display:"flex", alignItems:"center", justifyContent:"center", gap:6, whiteSpace:"nowrap", marginBottom:8 }}>
                    <span>{t.writeReview}</span>
                  </button>
                  <div style={{ display:"flex", gap:8 }}>
                    <div style={{ flex:1 }}><Button darkMode={darkMode} small secondary onClick={()=>{setStep("travel");setItinerary("");setSpots([]);setSpotsProgress(0);setSpotsTotal(0);setTripSaved(false);setCommunityShared(false);setLinkCopied(false);setChecklist({packing:[],before:[],after:[]});}}>{lang==="ko"?"🔄 재생성":"🔄 Regenerate"}</Button></div>
                    <div style={{ flex:1 }}><Button darkMode={darkMode} small secondary onClick={()=>{setStep("intro");setPersonal({name:"",age:"",gender:"",bloodType:"",mbti:"",zodiac:"",address:"",email:""});setTravel({startDate:"",endDate:"",destination:"",style:"",transport:""});setItinerary("");setSpots([]);setSpotsProgress(0);setSpotsTotal(0);}}>{lang==="ko"?"🏠 처음으로":"🏠 Home"}</Button></div>
                  </div>
                </div>
              </GlassCard>
            </div>
  );
}
